import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Avatar,
  Chip,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Groups as GroupsIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  ColorLens as ColorLensIcon
} from '@mui/icons-material';
import { taskDataService } from '../../../services/taskDataService';
import ReactAvatar from 'react-avatar';
import GuideButton from '../../common/guide/GuideButton';

// Checkbox icon
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Takım renk seçenekleri
const teamColors = [
  '#1976D2', // Mavi
  '#2E7D32', // Yeşil
  '#C62828', // Kırmızı
  '#7B1FA2', // Mor
  '#F57C00', // Turuncu
  '#0097A7', // Turkuaz
  '#455A64', // Gri-Mavi
  '#D81B60', // Pembe
  '#5D4037', // Kahverengi
  '#6A1B9A'  // Koyu Mor
];

interface CreateTeamModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  personnel: any[];
  teamSqlIdToEdit?: number | null; // Düzenlenecek ekip SQL id'si (opsiyonel)
  onSaved?: () => void;
}

interface TeamMember {
  id: string;        // personel firebaseUid
  name: string;
  duty: string;  // Ekipteki görevi 
  memberRole: string; // Lider, Görevli, İzleyici gibi roller
}

const CreateTeamModal: React.FC<CreateTeamModalProps> = ({
  open,
  onClose,
  companyId,
  personnel,
  teamSqlIdToEdit,
  onSaved
}) => {
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTeamData, setLoadingTeamData] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [teamColor, setTeamColor] = useState(teamColors[0]);
  // Düzenleme: silinen üyeleri tespit için orijinal üye uid'leri
  const [originalMemberUids, setOriginalMemberUids] = useState<string[]>([]);

  // Form doğrulama
  const [nameError, setNameError] = useState('');
  
  // Bildirim durumu için state ekle
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Ekip verilerini yükle (düzenleme modu için) — SQL (taskDataService)
  useEffect(() => {
    const loadTeamData = async () => {
      if (!teamSqlIdToEdit || !companyId || !open) return;
      
      setLoadingTeamData(true);
      
      try {
        const result = await taskDataService.getTeamGroup(teamSqlIdToEdit);
        if (result) {
          const { group, members } = result;
          setTeamName(group.name || '');
          setTeamDescription(group.description || '');
          setTeamColor(group.color || teamColors[0]);

          const membersArray: TeamMember[] = [];
          const selectedPersonnelArray: any[] = [];
          (members || []).forEach((m) => {
            const uid = m.personnelFirebaseUid;
            const personnelInfo = personnel.find(p => p.id === uid);
            membersArray.push({
              id: uid,
              name: m.name || personnelInfo?.name || 'İsimsiz',
              duty: m.duty || '',
              memberRole: m.memberRole || 'Görevli',
            });
            if (personnelInfo) selectedPersonnelArray.push(personnelInfo);
          });
          setTeamMembers(membersArray);
          setSelectedPersonnel(selectedPersonnelArray);
          setOriginalMemberUids(membersArray.map(m => m.id));
        } else {
          console.error('Ekip bulunamadı:', teamSqlIdToEdit);
          setNotification({ open: true, message: 'Ekip bilgileri yüklenemedi', severity: 'error' });
        }
      } catch (error) {
        console.error('Ekip verileri yüklenirken hata:', error);
        setNotification({ open: true, message: 'Ekip bilgileri yüklenirken bir hata oluştu', severity: 'error' });
      } finally {
        setLoadingTeamData(false);
      }
    };
    
    loadTeamData();
  }, [teamSqlIdToEdit, companyId, open, personnel]);

  // Sayfalama işlemleri
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Ekip üyesi görevini güncelle
  const handleDutyChange = (id: string, newDuty: string) => {
    setTeamMembers(prevMembers => 
      prevMembers.map(member => 
        member.id === id ? { ...member, duty: newDuty } : member
      )
    );
  };

  // Üye rolünü değiştirme
  const handleMemberRoleChange = (id: string, newMemberRole: string) => {
    setTeamMembers(prevMembers => 
      prevMembers.map(member => 
        member.id === id ? { ...member, memberRole: newMemberRole } : member
      )
    );
  };

  // Personel seçildiğinde
  const handlePersonnelChange = (_event: React.SyntheticEvent, newValue: any[]) => {
    setSelectedPersonnel(newValue);
    
    // Ekip üyelerini güncelle
    const currentMemberIds = teamMembers.map(member => member.id);
    
    // Yeni üyeleri ekle
    const newMembers = newValue
      .filter(person => !currentMemberIds.includes(person.id))
      .map(person => ({
        id: person.id,
        name: person.name,
        duty: '',
        memberRole: 'Görevli' // Varsayılan rol
      }));
    
    if (newMembers.length > 0) {
      setTeamMembers([...teamMembers, ...newMembers]);
    }
    
    // Kaldırılan üyeleri çıkar
    const selectedIds = newValue.map(person => person.id);
    setTeamMembers(prevMembers => 
      prevMembers.filter(member => selectedIds.includes(member.id))
    );
  };

  // Üye silme
  const handleRemoveMember = (id: string) => {
    setTeamMembers(prevMembers => prevMembers.filter(member => member.id !== id));
    setSelectedPersonnel(prevSelected => prevSelected.filter(person => person.id !== id));
  };

  // Renk değiştirme
  const handleColorChange = (color: string) => {
    setTeamColor(color);
  };

  // Ekip kaydetme
  const handleSaveTeam = async () => {
    // Doğrulama
    if (!teamName.trim()) {
      setNameError('Ekip adı gereklidir');
      return;
    }
    
    if (teamMembers.length === 0) {
      setNotification({
        open: true,
        message: 'En az bir ekip üyesi eklemelisiniz',
        severity: 'error'
      });
      return;
    }
    
    // Üye görevleri kontrolü
    const emptyDuties = teamMembers.filter(member => !member.duty?.trim());
    if (emptyDuties.length > 0) {
      setNotification({
        open: true,
        message: 'Tüm ekip üyelerinin bir görevi olmalıdır',
        severity: 'error'
      });
      return;
    }
    
    if (!companyId) {
      setNotification({
        open: true,
        message: 'Şirket bilgisi bulunamadı',
        severity: 'error'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      if (teamSqlIdToEdit) {
        // Mevcut ekibi güncelle (grup alanları)
        await taskDataService.updateTeamGroup({
          id: teamSqlIdToEdit,
          name: teamName,
          description: teamDescription,
          color: teamColor,
        });

        // Üye senkronu: form üyelerini upsert et
        for (const member of teamMembers) {
          await taskDataService.addTeamMember(teamSqlIdToEdit, {
            personnelFirebaseUid: member.id,
            name: member.name,
            duty: member.duty,
            memberRole: member.memberRole,
          });
        }
        // Çıkarılan üyeleri sil
        const currentUids = new Set(teamMembers.map(m => m.id));
        const removed = originalMemberUids.filter(uid => !currentUids.has(uid));
        for (const uid of removed) {
          await taskDataService.removeTeamMember(teamSqlIdToEdit, { personnelFirebaseUid: uid });
        }

        setNotification({ open: true, message: 'Ekip başarıyla güncellendi!', severity: 'success' });
      } else {
        // Yeni ekip oluştur (üyelerle birlikte)
        await taskDataService.createTeamGroup({
          name: teamName,
          description: teamDescription,
          color: teamColor,
          members: teamMembers.map(member => ({
            personnelFirebaseUid: member.id,
            name: member.name,
            duty: member.duty,
            memberRole: member.memberRole,
          })),
        });
        setNotification({ open: true, message: 'Ekip başarıyla oluşturuldu!', severity: 'success' });
      }

      onSaved?.();
      resetForm();
      onClose();
    } catch (error) {
      console.error('Ekip kaydedilirken hata:', error);
      setNotification({
        open: true,
        message: teamSqlIdToEdit ? 'Ekip güncellenirken bir hata oluştu' : 'Ekip oluşturulurken bir hata oluştu',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Bildirim kapatma fonksiyonu ekle
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Formu sıfırla
  const resetForm = () => {
    setTeamName('');
    setTeamDescription('');
    setTeamMembers([]);
    setSelectedPersonnel([]);
    setNameError('');
    setTeamColor(teamColors[0]);
    setPage(0);
    setOriginalMemberUids([]);
  };

  // Modal kapanırken formu sıfırla
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // Sayfalanmış ekip üyeleri
  const paginatedMembers = teamMembers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Sabit boyutta gösterilecek tablo satırları (boş satırları dolduracak)
  const displayRows = [...paginatedMembers];
  const emptyRows = rowsPerPage - displayRows.length;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          py: 1.5
        }}>
          <Typography variant="h6" fontWeight="bold">
            {teamSqlIdToEdit ? 'Ekip Düzenle' : 'Yeni Ekip Oluştur'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <GuideButton guideKey="tasks.createTeam" color="inherit" size="small" />
          <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ px: 2, py: 2 }}>
          {loadingTeamData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Ekip bilgileri yükleniyor...
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" color="primary" fontWeight="medium" gutterBottom>
                  Ekip Bilgileri
                </Typography>
                <Divider />
              </Box>
              
              {/* Ekip adı ve Personel filtresi yan yana */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Ekip Adı"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={teamName}
                    onChange={(e) => {
                      setTeamName(e.target.value);
                      if (e.target.value.trim()) setNameError('');
                    }}
                    error={!!nameError}
                    helperText={nameError}
                    placeholder="Satış Ekibi, Proje Ekibi vb."
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    multiple
                    id="personnel-selector"
                    options={personnel}
                    disableCloseOnSelect
                    value={selectedPersonnel}
                    onChange={handlePersonnelChange}
                    getOptionLabel={(option) => option.name}
                    size="small"
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                          size="small"
                        />
                        <Avatar 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            mr: 1, 
                            bgcolor: selected ? teamColor : 'grey.400',
                            fontSize: '0.8rem'
                          }}
                        >
                          {option.name[0]}
                        </Avatar>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Ekip Üyeleri"
                        placeholder="Personel ara..."
                        size="small"
                      />
                    )}
                    renderTags={() => null} // Seçilen personeller gösterilmiyor
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </Grid>
                
                {/* Ekip açıklaması */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Ekip Açıklaması"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    value={teamDescription}
                    onChange={(e) => setTeamDescription(e.target.value)}
                    placeholder="Ekip ne amaçla kuruluyor?"
                  />
                </Grid>
                
                {/* Ekip rengi ve sabit grup ikonu */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(5, 1fr)', 
                        gridTemplateRows: 'repeat(2, 1fr)',
                        gap: 1,
                        flexGrow: 1,
                        mr: 2
                      }}>
                        {teamColors.map((color) => (
                          <Box
                            key={color}
                            onClick={() => handleColorChange(color)}
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: color,
                              borderRadius: '50%',
                              cursor: 'pointer',
                              border: teamColor === color ? '2px solid #000' : 'none',
                              '&:hover': {
                                opacity: 0.8,
                              }
                            }}
                          />
                        ))}
                      </Box>
                      <Avatar 
                        sx={{ 
                          bgcolor: teamColor,
                          width: 56,
                          height: 56,
                          fontSize: '1.5rem'
                        }}
                      >
                        <GroupsIcon fontSize="large" />
                      </Avatar>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              
              {/* Seçilen personel sayısı - Her zaman göster */}
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Seçilen personel sayısı: <strong>{selectedPersonnel.length}</strong>
                </Typography>
              </Box>
              
              {/* Personel Tablosu - Her zaman sabit boyutta */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                  Ekip Üyeleri ve Görevleri
                </Typography>
                <TableContainer 
                  component={Paper} 
                  variant="outlined" 
                  sx={{ 
                    borderRadius: 2, 
                    minHeight: 200, // Sabit yükseklik (yaklaşık 5 satır için)
                    maxHeight: 200
                  }}
                >
                  <Table size="small" padding="none">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'background.default', height: 36 }}>
                        <TableCell width="30%" padding="checkbox" sx={{ pl: 1 }}>Personel</TableCell>
                        <TableCell width="35%" padding="checkbox">Ekipteki Görevi</TableCell>
                        <TableCell width="25%" padding="checkbox">Rol</TableCell>
                        <TableCell width="10%" align="center" padding="checkbox">İşlem</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayRows.map((member) => (
                        <TableRow key={member.id} sx={{ height: 42 }}>
                          <TableCell padding="checkbox" sx={{ pl: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar 
                                sx={{ 
                                  width: 24, 
                                  height: 24, 
                                  mr: 1, 
                                  bgcolor: teamColor,
                                  fontSize: '0.75rem'
                                }}
                              >
                                {member.name[0]}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{member.name}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell padding="checkbox">
                            <TextField
                              fullWidth
                              size="small"
                              variant="outlined"
                              placeholder="Ekipteki görevi"
                              value={member.duty || ''}
                              onChange={(e) => handleDutyChange(member.id, e.target.value)}
                              inputProps={{ style: { padding: '4px 8px', fontSize: '0.8rem' } }}
                            />
                          </TableCell>
                          <TableCell padding="checkbox">
                            <FormControl fullWidth size="small">
                              <Select
                                value={member.memberRole || 'Görevli'}
                                onChange={(e) => handleMemberRoleChange(member.id, e.target.value)}
                                sx={{ fontSize: '0.8rem' }}
                                inputProps={{ style: { padding: '4px 8px' } }}
                              >
                                <MenuItem value="Lider" sx={{ fontSize: '0.8rem' }}>Lider</MenuItem>
                                <MenuItem value="Görevli" sx={{ fontSize: '0.8rem' }}>Görevli</MenuItem>
                                <MenuItem value="İzleyici" sx={{ fontSize: '0.8rem' }}>İzleyici</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="center" padding="checkbox">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleRemoveMember(member.id)}
                              sx={{ padding: '2px' }}
                            >
                              <DeleteIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Boş satırlar ekleyerek sabit boyut sağla */}
                      {emptyRows > 0 && Array.from({ length: emptyRows }).map((_, index) => (
                        <TableRow key={`empty-${index}`} sx={{ height: 42 }}>
                          <TableCell colSpan={4} padding="none" />
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Sayfalama */}
                <TablePagination
                  component="div"
                  count={teamMembers.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10]}
                  labelRowsPerPage="Sayfa başına üye:"
                  labelDisplayedRows={({ from, to, count }) => 
                    `${count === 0 ? 0 : from}-${to} / ${count}`
                  }
                  sx={{ mt: 1 }}
                />
              </Box>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={onClose} 
            color="inherit"
            size="small"
          >
            İptal
          </Button>
          <Button 
            onClick={handleSaveTeam} 
            variant="contained" 
            color="primary"
            disabled={loading || loadingTeamData}
            startIcon={loading ? <CircularProgress size={16} /> : null}
            size="small"
          >
            {teamSqlIdToEdit ? 'Güncelle' : 'Ekibi Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Bildirim bileşeni ekle */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateTeamModal; 